import type { ReactNode } from "react";

import { Button } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

export type ArtifactCardButtonProps = {
	readonly name: string;
	readonly isLoading: boolean;
	readonly onClick: () => void;
	readonly isSelected?: boolean;
	readonly isDisabled?: boolean;
	readonly disabledTooltip?: string;
	readonly className?: string;
	readonly endComponent?: ReactNode;
};

export function ArtifactCardButton({
	name,
	isLoading,
	isSelected,
	isDisabled,
	disabledTooltip,
	className,
	endComponent,
	onClick,
}: ArtifactCardButtonProps) {
	function getTooltipContent(): { children: string } | undefined {
		if (isDisabled && disabledTooltip) {
			return { children: disabledTooltip };
		}

		if (endComponent || isSelected) {
			return undefined;
		}

		return { children: "Click to open artifact" };
	}

	return (
		<Button
			className={cn(
				"px-3 py-2 @max-sm/artifact-card:w-full",
				isSelected && "border-primary-border bg-primary",
				isDisabled && "cursor-not-allowed opacity-60",
				className,
			)}
			size="md"
			variant="outline"
			tooltip={getTooltipContent()}
			classNames={{
				wrapper: cn(
					isSelected && "!cursor-default [&_span]:!cursor-default",
					isDisabled && "!cursor-not-allowed [&_span]:!cursor-not-allowed",
					"@max-sm/artifact-card:[&_svg]:hidden",
				),
				base: "justify-start",
				text: "line-clamp-1 text-start @max-sm/artifact-card:text-sm",
			}}
			leftIcon={isLoading ? "spinner" : "fileAlt"}
			rightComponent={
				endComponent ? <span className="-mr-1 ml-auto">{endComponent}</span> : undefined
			}
			onClick={isSelected || isDisabled ? undefined : onClick}
		>
			{isLoading && !name ? "Loading..." : name}
		</Button>
	);
}
